/**
 * StatsManager - Centralized event bus for all analytics tracking
 * This is the single source of truth for all analytics events in the application
 * All tracking integrations (Statsig, Meta, Apphud, etc.) subscribe to this manager
 */

import type {
  AnalyticsEvent,
  EventName,
  EventData,
  EventSubscriber,
} from '../types/events.types';
import type { StatsDependencies } from '../types/dependencies.types';
import { normalizeEmail } from './user-id';

export class StatsManager {
  // Event storage - stores all events for replay to new subscribers
  private events: AnalyticsEvent[] = [];

  // Subscribers - analytics integrations that want to receive events
  private subscribers: EventSubscriber[] = [];

  // State tracking
  private initialized: boolean = false;
  private userIdentified: boolean = false;
  private currentUser: { email?: string; id?: string; identifiedAt?: string } | null = null;

  // Dependencies
  private deps: StatsDependencies = {};

  /**
   * Initialize StatsManager
   */
  init(deps: StatsDependencies = {}): void {
    // Store dependencies
    this.deps = {
      userRepository: deps.userRepository || null,
      flowManager: deps.flowManager || null,
      config: deps.config || null,
    };

    // Clear any previous state
    this.events = [];
    this.subscribers = [];
    this.userIdentified = false;
    this.currentUser = null;

    this.initialized = true;
    console.log('StatsManager initialized');

    // Track initial event
    this.track('stats_manager_initialized', {
      timestamp: new Date().toISOString(),
      environment: this.deps.config?.environment || 'unknown',
    });
  }

  /**
   * Subscribe to events
   * Callback receives: (eventName, eventData, eventMetadata, value)
   */
  subscribe(callback: EventSubscriber): () => void {
    if (typeof callback !== 'function') {
      console.error('StatsManager.subscribe requires a function');
      return () => {};
    }

    // Add to subscribers
    this.subscribers.push(callback);
    console.log(
      'New subscriber added to StatsManager. Total subscribers:',
      this.subscribers.length
    );

    // Replay all stored events to new subscriber
    this.replayEventsToSubscriber(callback);

    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
        console.log('Subscriber removed from StatsManager. Remaining:', this.subscribers.length);
      }
    };
  }

  /**
   * Replay stored events to a specific subscriber
   */
  private replayEventsToSubscriber(callback: EventSubscriber): void {
    if (this.events.length === 0) {
      return;
    }

    console.log(`Replaying ${this.events.length} events to new subscriber`);

    // Send each stored event to the new subscriber with correct parameter order
    this.events.forEach((event) => {
      try {
        // Match the dispatch signature: (name, data, metadata, value)
        callback(event.name, event.data, event.metadata, event.value);
      } catch (error) {
        console.error('Error replaying event to subscriber:', error);
      }
    });
  }

  /**
   * Core tracking method - all events go through here
   */
  track(eventName: EventName, data: EventData = {}, value: string | number | null = null): void {
    if (!this.initialized) {
      console.warn('StatsManager not initialized, skipping event:', eventName);
      return;
    }

    // Create event object
    const event: AnalyticsEvent = {
      name: eventName,
      data: { ...data },
      metadata: {
        timestamp: new Date().toISOString(),
        environment: this.deps.config?.environment || '',
        userIdentified: this.userIdentified,
        sequenceNumber: this.events.length + 1,
      },
      value,
    };

    // Add current screen if available
    if (this.deps.flowManager?.getCurrentScreenName) {
      try {
        const screenName = this.deps.flowManager.getCurrentScreenName();
        if (screenName) {
          event.metadata.currentScreen = screenName;
        }
      } catch (error) {
        console.warn('Failed to get current screen name:', error);
      }
    }

    // Add user info if identified
    if (this.currentUser) {
      event.metadata.userId = this.currentUser.email || this.currentUser.id;
    }

    // Store event
    this.events.push(event);

    // Log for debugging
    console.log('StatsManager event:', eventName, data);

    // Dispatch to all subscribers
    this.dispatch(event);
  }

  /**
   * Dispatch event to all subscribers
   */
  private dispatch(event: AnalyticsEvent): void {
    if (this.subscribers.length === 0) {
      return;
    }

    // Send to each subscriber
    this.subscribers.forEach((callback) => {
      try {
        callback(event.name, event.data, event.metadata, event.value);
      } catch (error) {
        console.error('Error dispatching event to subscriber:', error);
      }
    });
  }

  // === User Identification ===

  /**
   * Identify user (typically called when email is captured)
   */
  identifyUser(email: string, additionalData: EventData = {}): void {
    // Canonical identity: trim + lowercase, matching GA4's user_id hash and the Appwrite
    // backend, so every pixel (Meta/Apphud/ProfitWell/Statsig) identifies the same person.
    const normalized = normalizeEmail(email);
    if (this.userIdentified && this.currentUser?.email === normalized) {
      return; // Already identified with same email
    }

    this.currentUser = {
      email: normalized,
      ...additionalData,
      identifiedAt: new Date().toISOString(),
    };
    this.userIdentified = true;

    // Track identification event
    this.track('user_identified', {
      email: normalized,
      ...additionalData,
    });

    console.log('StatsManager user identified:', normalized);
  }

  // === Common Event Tracking Methods ===

  /**
   * Track screen view
   */
  trackScreenView(screenName: string, screenHash: string): void {
    this.track('screen_viewed', {
      screen: screenName,
      screenHash: screenHash,
    }, screenHash);
  }

  /**
   * Track email captured
   */
  trackEmailCaptured(email: string, additionalData: EventData = {}): void {
    // Normalize once here so the email_captured event + all downstream pixels use the
    // canonical (trim+lowercase) form. identifyUser normalizes again (idempotent).
    const normalized = normalizeEmail(email);
    this.track('email_captured', {
      email: normalized,
      ...additionalData,
    });

    // Also identify the user
    this.identifyUser(normalized, additionalData);
  }

  /**
   * Track funnel progression
   */
  trackFunnelStep(step: string, data: EventData = {}): void {
    this.track('funnel_step_completed', {
      step,
      ...data,
    });
  }

  /**
   * Track answer selection
   */
  trackAnswer(questionId: string, answer: string | string[], questionType: 'single' | 'multi' = 'single'): void {
    this.track('question_answered', {
      questionId,
      answer: Array.isArray(answer) ? answer.join(', ') : answer,
      questionType,
    });
  }

  /**
   * Track genre selection (multi-select)
   */
  trackGenreSelection(genres: string[]): void {
    this.track('genres_selected', {
      genres: Array.isArray(genres) ? genres.join(', ') : genres,
      genreCount: Array.isArray(genres) ? genres.length : 1,
    });
  }

  /**
   * Track practice commitment
   */
  trackPracticeCommitment(frequency: string): void {
    this.track('practice_commitment_selected', {
      frequency,
    });
  }

  /**
   * Track paywall view
   */
  trackPaywallView(timerStarted: boolean = false, additionalData: EventData = {}): void {
    const userData = this.deps.userRepository?.getAllData();
    this.track('paywall_viewed', {
      timerStarted,
      plan: userData?.plan || '',
      ...additionalData,
    });
  }

  /**
   * Track checkout initiated
   */
  trackCheckoutInitiated(price: number, currency: string = 'USD', additionalData: EventData = {}): void {
    const userData = this.deps.userRepository?.getAllData();
    this.track('checkout_initiated', {
      plan: userData?.plan || '',
      price,
      currency,
      ...additionalData,
    });
  }

  /**
   * Track country change
   */
  trackCountryChange(oldCountry: string, newCountry: string, oldCurrency: string, newCurrency: string): void {
    this.track('country_changed', {
      oldCountry,
      newCountry,
      oldCurrency,
      newCurrency,
    });
  }

  /**
   * Track subscription completed
   */
  trackSubscriptionCompleted(
    transactionId: string,
    amount: number,
    currency: string,
    additionalData: EventData = {}
  ): void {
    const userData = this.deps.userRepository?.getAllData();
    this.track('subscription_completed', {
      transactionId,
      amount,
      currency,
      plan: userData?.plan || '',
      ...additionalData,
    });
  }

  /**
   * Track timer events
   */
  trackTimerStarted(duration: number): void {
    this.track('timer_started', {
      durationMinutes: duration,
    });
  }

  trackTimerExpired(): void {
    this.track('timer_expired');
  }

  /**
   * Track CTA clicks
   */
  trackCTAClick(ctaName: string, location: string): void {
    this.track('cta_clicked', {
      ctaName,
      location,
    });
  }

  /**
   * Track email validation failure
   */
  trackEmailValidationFailed(email: string): void {
    this.track('email_validation_failed', {
      emailPattern: email.replace(/@.*/, '@***'), // Anonymize domain
    });
  }

  /**
   * Track direct paywall access
   */
  trackDirectPaywallAccess(source: string): void {
    this.track('direct_paywall_access', {
      source, // e.g., 'hash_routing', 'direct_link'
    });
  }

  /**
   * Track navigation back
   */
  trackNavigationBack(fromScreen: string, toScreen: string): void {
    this.track('navigation_back', {
      fromScreen,
      toScreen,
    });
  }

  /**
   * Track plan selection change
   */
  trackPlanChange(oldPlan: string, newPlan: string): void {
    this.track('plan_changed', {
      oldPlan,
      newPlan,
    });
  }

  /**
   * Track family plan toggle
   */
  trackFamilyPlanToggle(enabled: boolean): void {
    this.track('family_plan_toggled', {
      enabled,
    });
  }

  // === Utility Methods ===

  /**
   * Get all stored events (for debugging/testing)
   */
  getAllEvents(): AnalyticsEvent[] {
    return [...this.events]; // Return copy to prevent modification
  }

  /**
   * Clear all events (useful for testing)
   */
  clearEvents(): void {
    this.events = [];
    console.log('StatsManager events cleared');
  }

  /**
   * Get event count
   */
  getEventCount(): number {
    return this.events.length;
  }

  /**
   * Get subscriber count
   */
  getSubscriberCount(): number {
    return this.subscribers.length;
  }

  /**
   * Update dependencies (for circular dependency resolution)
   */
  updateDependencies(deps: Partial<StatsDependencies>): void {
    this.deps = { ...this.deps, ...deps };
  }

  /**
   * Export events as JSON (for debugging)
   */
  exportEvents(): string {
    return JSON.stringify(this.events, null, 2);
  }

  /**
   * Get events by type
   */
  getEventsByType(eventName: EventName): AnalyticsEvent[] {
    return this.events.filter((event) => event.name === eventName);
  }
}

// Create singleton instance
const statsManager = new StatsManager();

// Make globally available for debugging
if (typeof window !== 'undefined') {
  (window as any).StatsManager = statsManager;
}

export default statsManager;
